// =============================================================================
// io/sync/deleteAccount.ts — the one irreversible action on the Accounts card.
// -----------------------------------------------------------------------------
// A hard delete of the account row, matching the old client. What makes this
// file mostly about COUNTING rather than deleting is that the row is not the
// only thing that goes.
//
// ── 🚨 TWO FOREIGN KEYS, TWO OPPOSITE OUTCOMES, AND ONLY ONE IS WARNED ABOUT ──
//
//   trades.account_id            ON DELETE SET NULL   → trades SURVIVE, unassigned
//   account_adjustments.account_id  ON DELETE CASCADE → fee rows are DESTROYED
//
// The old client's confirm reads, in full: **"Delete this account? Trades stay
// but become unassigned."** That is true and it is half the story. It says
// nothing about the adjustments, which are eval fees, activation fees, resets
// and payouts — the ONLY record of money that left the trader's bank, and the
// only expense input the Economics tab has.
//
// On Josh's book, deleting `PA-APEX-35531-09` would take its **$99 activation
// fee** with it — 32% of his $310 lifetime spend — and nothing on screen would
// say so before or after. That is the gap this module exists to close: it counts
// what will be lost FIRST, so the confirmation can name it.
//
// ── ⚠ THE TRADES ARE SAFER THAN THEY LOOK, AND THAT IS WORTH KNOWING TOO ──
//
// An unassigned trade keeps its stored `fees` and `netPnl`. The commission
// re-derivation looks its rate up through the account's FIRM, and
// `applyCommission` returns the trade UNCHANGED when no rate is found — so
// losing the account does not silently re-price its trades to gross. Checked,
// because the opposite would have been a data-corrupting delete rather than a
// tidy-up.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import { reportFailureAs } from '../diagnostics/errorLog';

/**
 * What a delete will take with it. Counted BEFORE anything is written, so the
 * confirmation can state it.
 */
export interface DeleteImpact {
  accountId: string;
  /** Trades that will be unassigned. They survive. */
  trades: number;
  /** Fee / payout rows that will be DESTROYED. They do not. */
  adjustments: number;
  /** The absolute money those rows record — what Economics loses. */
  adjustmentValue: number;
}

export interface DeleteResult {
  ok: boolean;
  error: string | null;
  /** What was actually removed, for the message afterwards. */
  impact?: DeleteImpact;
}

export interface DeleteOptions {
  dryRun?: boolean;
}

/**
 * Count what a delete would cost.
 *
 * ⚠ A FAILED COUNT MUST BLOCK THE DELETE, not proceed with an optimistic zero.
 * "0 fee records" and "we could not check" look identical in a dialog, and the
 * first is a promise the second cannot make.
 */
export async function countDeleteImpact(
  sb: SupabaseClient,
  accountId: string,
): Promise<{ impact: DeleteImpact | null; error: string | null }> {
  if (!accountId) return { impact: null, error: 'No account id.' };

  const { data: trades, error: tradeError } = await sb
    .from('trades')
    .select('id')
    .eq('account_id', accountId);

  if (tradeError) {
    // ⚠ The two counts show the user the SAME sentence, so the scope is the only
    // thing in the log that says which of the two queries actually failed.
    return {
      impact: null,
      error: reportFailureAs(
        'count affected trades', tradeError,
        `Couldn’t check what this would affect: ${tradeError.message}`,
      ),
    };
  }

  const { data: adjustments, error: adjError } = await sb
    .from('account_adjustments')
    .select('amount')
    .eq('account_id', accountId);

  if (adjError) {
    return {
      impact: null,
      error: reportFailureAs(
        'count affected fee records', adjError,
        `Couldn’t check what this would affect: ${adjError.message}`,
      ),
    };
  }

  const rows = (adjustments ?? []) as { amount: unknown }[];
  return {
    impact: {
      accountId,
      trades: (trades ?? []).length,
      adjustments: rows.length,
      // ⚠ ABSOLUTE. Fees are stored negative and payouts positive; the figure
      // being reported is "how much money history disappears", not a net.
      adjustmentValue: rows.reduce((s, r) => s + Math.abs(Number(r.amount) || 0), 0),
    },
    error: null,
  };
}

/**
 * Delete the account row. The database cascades and nulls from there.
 *
 * ⚠ IT DOES NOT DELETE THE TRADES ITSELF, and must not. `ON DELETE SET NULL` is
 * the schema's promise that a trade outlives its account; a client-side sweep
 * that removed them would be a different, unrecoverable action wearing the same
 * button.
 *
 * ⚠ `impact` is required rather than recounted here. The caller has already
 * shown those numbers to the user and got a yes; recounting would let the answer
 * drift between the dialog and the write, so what is deleted is what was agreed.
 */
export async function deleteAccount(
  sb: SupabaseClient,
  impact: DeleteImpact,
  opts: DeleteOptions = {},
): Promise<DeleteResult> {
  if (!impact?.accountId) {
    return { ok: false, error: 'Refusing to delete an account with no id.' };
  }
  if (opts.dryRun) return { ok: true, error: null, impact };

  const { data, error } = await sb
    .from('accounts')
    .delete()
    .eq('id', impact.accountId)
    .select('id');

  if (error) {
    return {
      ok: false,
      error: reportFailureAs(
        'delete account', error,
        `Couldn’t delete the account: ${error.message}`,
      ),
    };
  }
  if (!data || !data.length) {
    // ⚠ NOT a silent success. A DELETE against a row RLS hides removes nothing
    // and reports no error, so without this the card would vanish from the
    // screen and reappear on the next load.
    return {
      ok: false,
      error: 'That delete matched no rows — nothing was removed. Reload and try again.',
    };
  }
  return { ok: true, error: null, impact };
}
